import { useMemo, useState, type DragEvent } from "react";
import { Badge, Button, Checkbox, EmptyState, QueryStateBoundary } from "@/components/ui";
import { STATUSES, STATUS_LABELS, type OutreachStatus } from "@/api/types/outreach";
import { useOutreachBoard, usePatchOutreach } from "@/api/hooks/useOutreach";
import { OutreachCard } from "./OutreachCard";
import { OutreachFormDialog } from "./OutreachFormDialog";
import { OutreachDetailDialog } from "./OutreachDetailDialog";
import { RhythmWidgets } from "./RhythmWidgets";
import { toastError } from "@/components/ui/Toast";
import { getErrorMessage } from "@/api/client";

/**
 * Phase 7's outreach & nurture board: the weekly rhythm quotas up top, then
 * a 7-column kanban (Identified -> ... -> Dead) over the outreach funnel.
 *
 * Seeded planner/venue targets came from an LLM playbook and are unverified
 * until Chris confirms each one — the "Show unverified only" filter and the
 * per-card "⚠ verify" chip both exist to keep that risk visible rather than
 * letting a seeded row look like a real, checked lead.
 */
export function OutreachPage() {
  const { data, isLoading, error, refetch } = useOutreachBoard();
  const patchOutreach = usePatchOutreach();

  const [formOpen, setFormOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);

  function handleDragStart(e: DragEvent<HTMLDivElement>, id: string) {
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "move";
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }

  function handleDrop(e: DragEvent<HTMLDivElement>, status: OutreachStatus) {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;

    const currentStatus = data?.columns.find((col) =>
      col.items.some((item) => item.id === id),
    )?.status;
    if (currentStatus === status) return;

    patchOutreach.mutate(
      { id, status },
      { onError: (err) => toastError("Couldn't move outreach target", getErrorMessage(err)) },
    );
  }

  const columns = useMemo(() => {
    if (!data) return [];
    if (!unverifiedOnly) return data.columns;
    return data.columns.map((col) => ({
      ...col,
      items: col.items.filter((item) => !item.verified),
    }));
  }, [data, unverifiedOnly]);

  const totalItems = columns.reduce((sum, col) => sum + col.items.length, 0);
  const totalUnfiltered = data?.columns.reduce((sum, col) => sum + col.items.length, 0) ?? 0;

  return (
    <div className="flex h-full flex-col p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Outreach</h1>
        <Button onClick={() => setFormOpen(true)}>New outreach target</Button>
      </div>

      <div className="mb-6">
        <RhythmWidgets />
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {totalUnfiltered === 0 ? (
          <EmptyState
            icon="🤝"
            title="No outreach targets yet"
            description="Planners, venues, and corporate contacts you're nurturing land here."
            action={{ label: "New outreach target", onClick: () => setFormOpen(true) }}
          />
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <Checkbox
                label="Show unverified only"
                checked={unverifiedOnly}
                onChange={(e) => setUnverifiedOnly(e.target.checked)}
              />
              {unverifiedOnly && (
                <Badge variant="warning" size="sm">
                  {totalItems} unverified
                </Badge>
              )}
            </div>

            <div className="flex-1 overflow-x-auto pb-4">
              <div className="flex min-h-full gap-4">
                {STATUSES.map((status) => {
                  const items =
                    columns.find((col) => col.status === status)?.items ?? [];
                  return (
                    <div
                      key={status}
                      data-status-column={status}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, status)}
                      className="flex w-72 shrink-0 flex-col rounded-lg bg-bg-muted/50"
                    >
                      <div className="flex items-center justify-between px-3 py-2.5">
                        <h2 className="text-sm font-semibold text-text-primary">
                          {STATUS_LABELS[status]}
                        </h2>
                        <span className="rounded-full bg-bg-muted px-2 py-0.5 text-xs font-medium text-text-secondary">
                          {items.length}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col gap-2 px-2 pb-2">
                        {items.map((item) => (
                          <OutreachCard
                            key={item.id}
                            item={item}
                            onOpen={() => setSelectedId(item.id)}
                            onDragStart={(e) => handleDragStart(e, item.id)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </QueryStateBoundary>

      <OutreachFormDialog open={formOpen} onClose={() => setFormOpen(false)} />

      <OutreachDetailDialog outreachId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
